import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';
import { useAuth } from '../../context/AuthContext';
import { consultEncyclopediaAI } from '../../services/aiService';

const EncyclopediaModal = ({ onClose }) => {
  const { state, updateEncyclopedia } = useStory();
  const { user } = useAuth();
  const [entries, setEntries] = useState(state.encyclopedia || []);
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'edit', 'import', 'consult'
  const [editingEntry, setEditingEntry] = useState(null);
  const [importText, setImportText] = useState('');
  const [consultFeedback, setConsultFeedback] = useState('');
  const [isConsulting, setIsConsulting] = useState(false);

  // Only owners (or authorized collaborators) should see the edit tools.
  const isOwner = state.ownerId === user?.id;

  const handleSaveEntry = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newEntry = {
      id: editingEntry?.id || Date.now().toString(),
      category: formData.get('category'),
      title: formData.get('title'),
      description: formData.get('description'),
    };

    let newEntries;
    if (editingEntry) {
      newEntries = entries.map(ent => ent.id === newEntry.id ? newEntry : ent);
    } else {
      newEntries = [...entries, newEntry];
    }
    setEntries(newEntries);
    updateEncyclopedia(newEntries);
    setEditingEntry(null);
    setActiveTab('list');
  };

  const handleDeleteEntry = (id) => {
    if(window.confirm("Delete this lore entry?")) {
        const newEntries = entries.filter(e => e.id !== id);
        setEntries(newEntries);
        updateEncyclopedia(newEntries);
    }
  };

  const handleImport = () => {
    try {
        const parsed = JSON.parse(importText);
        if(Array.isArray(parsed)) {
            const newEntries = [...entries, ...parsed];
            setEntries(newEntries);
            updateEncyclopedia(newEntries);
            setActiveTab('list');
            setImportText('');
        } else {
            alert("Import must be a JSON array.");
        }
    } catch (e) {
        alert("Invalid JSON format.");
    }
  };

  const handleConsultAI = async () => {
    setIsConsulting(true);
    setConsultFeedback('');
    try {
        const feedback = await consultEncyclopediaAI(entries, state.ai.apiKey, state.ai.model);
        setConsultFeedback(feedback);
    } catch(e) {
        setConsultFeedback("Failed to consult AI.");
    }
    setIsConsulting(false);
  };

  // Group entries by category
  const groupedEntries = entries.reduce((acc, entry) => {
      if(!acc[entry.category]) acc[entry.category] = [];
      acc[entry.category].push(entry);
      return acc;
  }, {});

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-slate-900 w-full max-w-4xl h-[85vh] rounded-2xl shadow-2xl flex flex-col border border-gray-200 dark:border-purple-800/50 overflow-hidden"
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-purple-800/50 bg-gray-100 dark:bg-purple-900/20">
            <div>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white flex items-center space-x-3">
                <SafeIcon icon={FiIcons.FiBookOpen} className="text-amber-500" />
                <span>World Encyclopedia</span>
              </h3>
              <p className="text-sm text-gray-900 mt-1 dark:text-purple-400">
                Lore, rules, and secrets of {state.storyTitle}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 bg-white hover:bg-purple-50 dark:bg-slate-800 dark:hover:bg-slate-700 text-[#333] dark:text-purple-300 rounded-lg transition-colors border border-gray-200 dark:border-purple-800"
            >
              <SafeIcon icon={FiIcons.FiX} />
            </button>
          </div>

          <div className="flex flex-1 overflow-hidden">
             {/* Sidebar Navigation */}
             <div className="w-48 sm:w-64 border-r border-gray-200 dark:border-purple-800/50 bg-purple-50/30 dark:bg-purple-900/10 p-4 flex flex-col space-y-2">
                 <button onClick={() => setActiveTab('list')} className={`px-4 py-2 text-left rounded-lg text-sm font-bold transition-colors ${activeTab === 'list' ? 'bg-purple-100 text-purple-700 dark:bg-purple-800/50 dark:text-purple-200' : 'text-[#333] dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30'}`}>View Lore</button>
                 {isOwner && (
                    <>
                        <button onClick={() => { setEditingEntry(null); setActiveTab('edit'); }} className={`px-4 py-2 text-left rounded-lg text-sm font-bold transition-colors ${activeTab === 'edit' ? 'bg-gray-100 text-purple-700 dark:bg-purple-800/50 dark:text-purple-200' : 'text-gray-900 dark:text-purple-400 hover:bg-gray-50 dark:hover:bg-purple-900/30'}`}>+ Add Entry</button>
                        <button onClick={() => setActiveTab('import')} className={`px-4 py-2 text-left rounded-lg text-sm font-bold transition-colors ${activeTab === 'import' ? 'bg-gray-100 text-purple-700 dark:bg-purple-800/50 dark:text-purple-200' : 'text-gray-900 dark:text-purple-400 hover:bg-gray-50 dark:hover:bg-purple-900/30'}`}>Import JSON</button>
                        <button onClick={() => { setActiveTab('consult'); handleConsultAI(); }} className={`px-4 py-2 text-left rounded-lg text-sm font-bold transition-colors mt-auto border border-amber-200 dark:border-amber-900 ${activeTab === 'consult' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200' : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30'}`}>
                            <SafeIcon icon={FiIcons.FiCpu} className="mr-2 inline" />
                            Consult AI
                        </button>
                    </>
                 )}
             </div>

             {/* Content Area */}
             <div className="flex-1 p-6 overflow-y-auto">
                 {activeTab === 'list' && (
                     <div className="space-y-8">
                         {Object.keys(groupedEntries).length === 0 ? (
                             <div className="text-center p-12 opacity-50">
                                 <SafeIcon icon={FiIcons.FiBook} className="text-4xl mx-auto mb-4" />
                                 <p>No encyclopedia entries yet.</p>
                             </div>
                         ) : (
                             Object.entries(groupedEntries).map(([category, catEntries]) => (
                                 <div key={category} className="space-y-4">
                                     <h4 className="text-xl font-serif font-bold text-purple-700 dark:text-purple-300 border-b border-gray-200 dark:border-purple-800 pb-2">{category}</h4>
                                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                         {catEntries.map(entry => (
                                             <div key={entry.id} className="p-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-purple-800/50 rounded-xl relative group">
                                                 {isOwner && (
                                                     <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                                                         <button onClick={() => { setEditingEntry(entry); setActiveTab('edit'); }} className="p-1.5 text-gray-600 hover:text-amber-500 bg-gray-50 dark:bg-slate-700 rounded"><SafeIcon icon={FiIcons.FiEdit2} className="text-xs" /></button>
                                                         <button onClick={() => handleDeleteEntry(entry.id)} className="p-1.5 text-gray-600 hover:text-red-500 bg-gray-50 dark:bg-slate-700 rounded"><SafeIcon icon={FiIcons.FiTrash2} className="text-xs" /></button>
                                                     </div>
                                                 )}
                                                 <h5 className="font-bold text-gray-900 dark:text-white mb-2">{entry.title}</h5>
                                                 <p className="text-sm text-gray-900 dark:text-slate-300 whitespace-pre-wrap">{entry.description}</p>
                                             </div>
                                         ))}
                                     </div>
                                 </div>
                             ))
                         )}
                     </div>
                 )}

                 {activeTab === 'edit' && isOwner && (
                     <form onSubmit={handleSaveEntry} className="max-w-2xl mx-auto space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-[#333] dark:text-purple-300 mb-2">Category</label>
                            <input name="category" defaultValue={editingEntry?.category || ''} placeholder="e.g., Races, Magic System, Factions" className="w-full bg-gray-100 dark:bg-purple-900/20 text-[#333] dark:text-white p-3 rounded-xl border border-gray-200 dark:border-purple-800 focus:outline-none focus:ring-2 focus:ring-amber-500" required />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-[#333] dark:text-purple-300 mb-2">Title</label>
                            <input name="title" defaultValue={editingEntry?.title || ''} placeholder="Entry Title" className="w-full bg-gray-100 dark:bg-purple-900/20 text-[#333] dark:text-white p-3 rounded-xl border border-gray-200 dark:border-purple-800 focus:outline-none focus:ring-2 focus:ring-amber-500" required />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-[#333] dark:text-purple-300 mb-2">Description</label>
                            <textarea name="description" defaultValue={editingEntry?.description || ''} rows={8} placeholder="Detailed lore description..." className="w-full bg-gray-100 dark:bg-purple-900/20 text-[#333] dark:text-white p-3 rounded-xl border border-gray-200 dark:border-purple-800 focus:outline-none focus:ring-2 focus:ring-amber-500" required />
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button type="button" onClick={() => setActiveTab('list')} className="px-6 py-2 text-gray-900 dark:text-purple-300 font-bold hover:bg-gray-50 dark:hover:bg-purple-900/30 rounded-xl">Cancel</button>
                            <button type="submit" className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-md transition-colors">Save Entry</button>
                        </div>
                     </form>
                 )}

                 {activeTab === 'import' && isOwner && (
                     <div className="max-w-2xl mx-auto space-y-6">
                         <div>
                             <label className="block text-sm font-bold text-[#333] dark:text-purple-300 mb-2">Paste JSON Array</label>
                             <textarea value={importText} onChange={e => setImportText(e.target.value)} rows={10} placeholder={'[\n  { "category": "Magic", "title": "Fireball", "description": "Burns stuff." }\n]'} className="w-full bg-gray-100 dark:bg-purple-900/20 text-xs font-mono text-[#333] dark:text-white p-4 rounded-xl border border-gray-200 dark:border-purple-800 focus:outline-none focus:ring-2 focus:ring-amber-500" />
                         </div>
                         <div className="flex justify-end">
                             <button onClick={handleImport} className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-md transition-colors">Import Data</button>
                         </div>
                     </div>
                 )}

                 {activeTab === 'consult' && isOwner && (
                     <div className="max-w-3xl mx-auto space-y-6">
                         <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 rounded-xl flex items-start space-x-4">
                             <div className="p-3 bg-amber-100 dark:bg-amber-900/50 rounded-full text-amber-600 dark:text-amber-400">
                                 <SafeIcon icon={FiIcons.FiCpu} className="text-xl" />
                             </div>
                             <div>
                                 <h4 className="font-bold text-amber-800 dark:text-amber-300">AI Lore Consultant</h4>
                                 <p className="text-sm text-amber-700 dark:text-amber-400/80 mt-1">The AI will analyze your encyclopedia for logical fallacies, inconsistencies, and suggest real-world connections to make your world more believable.</p>
                             </div>
                         </div>

                         {isConsulting ? (
                             <div className="text-center py-12">
                                 <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-4xl text-amber-500 mx-auto mb-4" />
                                 <p className="text-gray-900 dark:text-purple-300 animate-pulse">Consulting the ancient tomes...</p>
                             </div>
                         ) : consultFeedback ? (
                             <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-purple-800/50 p-6 rounded-xl prose dark:prose-invert max-w-none">
                                 <div dangerouslySetInnerHTML={{ __html: consultFeedback.replace(/\n/g, '<br/>') }} />
                             </div>
                         ) : (
                             <div className="text-center py-12 opacity-50">
                                <p>Click Consult AI on the left to begin.</p>
                             </div>
                         )}

                         {!isConsulting && consultFeedback && (
                             <div className="flex justify-center mt-6">
                                 <button onClick={handleConsultAI} className="px-6 py-2 bg-gray-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50 font-bold rounded-xl transition-colors flex items-center space-x-2">
                                     <SafeIcon icon={FiIcons.FiRefreshCw} />
                                     <span>Consult Again</span>
                                 </button>
                             </div>
                         )}
                     </div>
                 )}
             </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EncyclopediaModal;
